import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal.tsx';
import { deckNameError, deckSlugFor, normaliseDeckName, isReservedDeckSlug, MAX_DECK_NAME } from '../lib/format.ts';
import type { DeckSummary } from '../../shared/deck.ts';

/**
 * Name a deck — the dialog behind both Rename and Duplicate.
 *
 * The user types a NAME; the slug is derived from it and shown, never typed.
 * That is the whole point of the feature: before this, Duplicate asked for a raw
 * slug and Rename only touched the title, so the two drifted apart until
 * production held `rkh-touchstone-market-feedback-copy-copy-copy` titled
 * "rkh-touchstone-market-feedback-final- version".
 *
 * Validation runs live against `decks` (the dashboard already has every deck
 * loaded, so a name clash needs no round-trip). The server re-checks everything
 * — this is here to explain the problem before the user commits, not to be the
 * gate.
 *
 */
type DeckNameModalProps = {
  mode: 'rename' | 'duplicate';
  /** The deck being renamed or copied. */
  deck?: Pick<DeckSummary, 'slug' | 'title' | 'publishedAt'> | null;
  /** Every deck, for the live conflict check. */
  decks?: DeckSummary[];
  /** From GET /api/decks — keeps the slug preview honest if a host overrides
   *  STUDIO_DECK_SLUG_PREFIX. */
  slugPrefix?: string;
  /** Duplicate's suggested name (server-computed nextFreeName). */
  initialName?: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (name: string) => void;
};

export default function DeckNameModal({
  mode,
  deck,
  decks = [],
  slugPrefix = 'rkh-',
  initialName,
  busy,
  onCancel,
  onConfirm,
}: DeckNameModalProps) {
  const renaming = mode === 'rename';
  const [draft, setDraft] = useState(initialName ?? (renaming ? deck?.title || '' : ''));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const name = normaliseDeckName(draft);
  const slug = useMemo(() => deckSlugFor(name, slugPrefix), [name, slugPrefix]);

  // Reserved slugs and the library's namespace, both mirrored from the server
  // in lib/format.ts. ("Library" derives to rkh-library, the retired shelf.)
  const reserved = isReservedDeckSlug(slug);

  const error = useMemo(() => {
    const bad = deckNameError(draft);
    if (bad) return bad;
    if (reserved) return `"${name}" is reserved: choose a different name.`;
    const lower = name.toLowerCase();
    // A rename excludes the deck itself; a duplicate excludes nothing, since the
    // copy is a genuinely new deck competing with every existing one.
    const others = renaming ? decks.filter((d) => d.slug !== deck?.slug) : decks;
    for (const d of others) {
      if (normaliseDeckName(d.title || '').toLowerCase() === lower) {
        return `A deck called "${d.title}" already exists.`;
      }
      if (d.slug === slug) {
        return `That name would clash with the existing deck "${d.title}" (${d.slug}).`;
      }
    }
    return null;
  }, [draft, name, slug, decks, deck, renaming, reserved]);

  // A rename that leaves the slug alone (fixing capitalisation, say) changes no
  // URL and takes nothing offline — so it must not threaten the user with an
  // unpublish it isn't going to do.
  const slugChanges = renaming && slug !== deck?.slug;
  const warnsUnpublish = slugChanges && Boolean(deck?.publishedAt);

  const canSubmit = !busy && !error && name.length > 0 && (!renaming || name !== normaliseDeckName(deck?.title || ''));

  function submit() {
    if (!canSubmit) return;
    onConfirm(name);
  }

  return (
    <Modal
      title={renaming ? 'Rename deck' : 'Duplicate deck'}
      className="modal--confirm"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={submit} disabled={!canSubmit}>
            {renaming ? 'Rename' : 'Duplicate'}
          </button>
        </>
      }
    >
      <label className="muted small" htmlFor="deck-name-input">
        Name
      </label>
      <input
        id="deck-name-input"
        ref={inputRef}
        type="text"
        className="modal__input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Escape is Modal's, not this input's — handling it here as
          // well called onCancel twice for one keystroke.
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        maxLength={MAX_DECK_NAME}
        aria-describedby="deck-name-slug"
        aria-invalid={error ? 'true' : undefined}
      />

      <p className="muted small" id="deck-name-slug" style={{ marginTop: '0.5rem' }}>
        {slug ? (
          <>
            Link <code>/p/{slug}/</code> <span style={{ opacity: 0.7 }}>(from the name)</span>
          </>
        ) : (
          'The share link is made from the name.'
        )}
      </p>

      {error && (
        <p className="small" role="alert" style={{ marginTop: '0.5rem', color: 'var(--danger, #ff4d6d)' }}>
          {error}
        </p>
      )}

      {warnsUnpublish && !error && (
        <p
          className="small"
          style={{
            marginTop: '0.75rem',
            padding: '0.6rem 0.7rem',
            borderRadius: '0.4rem',
            border: '1px solid rgba(255, 176, 32, 0.5)',
            background: 'rgba(255, 176, 32, 0.08)',
          }}
        >
          This deck is published. Renaming changes its link and unpublishes it: anyone holding the old link will need
          the new one after you republish.
        </p>
      )}

      {!renaming && (
        <p className="muted small" style={{ marginTop: '0.75rem' }}>
          The copy starts unpublished, with its own share link once you publish it.
        </p>
      )}
    </Modal>
  );
}
